import { getBorderStyle, getBorderWidth } from '$lib/global/helper/border';
import { getBorderRadius } from '$lib/global/helper/border-radius';
import { getShadow } from '$lib/global/helper/shadow';
import { getColor, getColorByLightness } from '$lib/helper';

import type {
    BrandBorderRadius,
    BrandBorderStyle,
    BrandBorderWidth,
    BrandShadow,
    Theme,
} from '$lib/types/Theme';

export interface ResolvedWebinarStyling {
    buttonBgColor: string;
    buttonTextColor: string;
    borderColor: string | undefined;
    containerBorderRadiusClass: string;
    buttonBorderRadiusClass: string;
    shadowClass: string | undefined;
    borderWidthClass: string | undefined;
    borderStyleClass: string | undefined;
}

interface Params {
    theme: Theme | undefined;
    backgroundColor: string | undefined;
    color: string | undefined;
}

const isNonTransparent = (value: string | undefined): value is string =>
    !!value && value !== 'transparent';

const resolveButtonBgColor = (
    backgroundColor: string | undefined,
    theme: Theme | undefined,
): string => {
    if (isNonTransparent(backgroundColor)) return backgroundColor;

    if (theme?.buttonBackgroundColor) return theme.buttonBackgroundColor;

    return getColor(backgroundColor ?? '', theme?.buttonBackgroundColor ?? '#0076FF');
};

const resolveButtonTextColor = (
    color: string | undefined,
    theme: Theme | undefined,
    buttonBgColor: string,
): string => {
    if (isNonTransparent(color)) return color;

    if (theme?.primaryText) return theme.primaryText;

    return getColor(color ?? '', getColorByLightness(buttonBgColor));
};

export const resolveWebinarStyling = ({
    theme,
    backgroundColor,
    color,
}: Params): ResolvedWebinarStyling => {
    const isThemeV2 = !!theme?.surface;

    const buttonBgColor = resolveButtonBgColor(backgroundColor, theme);
    const buttonTextColor = resolveButtonTextColor(color, theme, buttonBgColor);
    const borderColor = theme?.border;

    const containerBorderRadiusClass = isThemeV2
        ? getBorderRadius('webinarFrame', theme?.borderRadius as BrandBorderRadius | undefined)
        : getBorderRadius('video');

    const buttonBorderRadiusClass = isThemeV2
        ? getBorderRadius('webinarButton', theme.borderRadius as BrandBorderRadius)
        : 'fun-rounded-[6px]';

    const shadowClass = isThemeV2
        ? getShadow('webinarFrame', theme.shadow as BrandShadow)
        : undefined;
    const borderWidthClass = isThemeV2
        ? getBorderWidth(theme.borderWidth as BrandBorderWidth)
        : undefined;
    const borderStyleClass = isThemeV2
        ? getBorderStyle(theme?.borderStyle as BrandBorderStyle)
        : undefined;

    return {
        buttonBgColor,
        buttonTextColor,
        borderColor,
        containerBorderRadiusClass,
        buttonBorderRadiusClass,
        shadowClass,
        borderWidthClass,
        borderStyleClass,
    };
};
